import React, { useEffect, useState } from "react";
import Select from "react-select";
import { Button, Grid } from "@mui/material";
import SoftBox from "components/SoftBox";
import FormField from "layouts/pages/account/components/FormField";
import { useAppServices } from "hook/services";
import { useAgencyInfo } from "context/agency";
import deleteIcon from "../../../../../../../assets/themeIcons/delete-icon.svg";
import editIcon from "../../../../../../../assets/themeIcons/edit-icon.svg";
import toast from "react-hot-toast";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import dragImage from "../../../../../../../assets/images/drag.png";

function SubCategoryManager({ handleRefresh, categories }) {
  const AppService = useAppServices();
  const [agency] = useAgencyInfo();

  const [subCategories, setSubCategories] = useState([]);
  const [tagsOptions, setTagsOptions] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    category: null,
    tags: [],
  });

  // Fetch subcategories
  const getSubCategories = async () => {
    try {
      const { response } = await AppService.topnav_sub_categories.get({
        query: `agency_id=${agency._id}`,
      });
      setSubCategories(response?.data || []);
    } catch (error) {
      console.error("Error fetching subcategories:", error);
    }
  };

  // Fetch tags
  const getTags = async () => {
    try {
      const { response } = await AppService.business_tags.get({
        query: `agency_id=${agency._id}`,
      });
      const formattedTags =
        response?.data?.map((tag) => ({
          value: tag._id,
          label: tag.name,
        })) || [];
      setTagsOptions(formattedTags);
    } catch (error) {
      console.error("Error fetching tags:", error);
    }
  };

  useEffect(() => {
    getSubCategories();
    getTags();
  }, []);

  // Handle form changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCategoryChange = (selectedOption) => {
    setFormData((prev) => ({ ...prev, category: selectedOption }));
  };

  const handleTagChange = (selectedOptions) => {
    setFormData((prev) => ({
      ...prev,
      tags: selectedOptions ? selectedOptions.map((option) => option.value) : [],
    }));
  };

  // Add or update subcategory
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let response;
      const payload = {
        name: formData.name,
        category: formData.category ? formData.category.value : null,
        tags: formData.tags,
        agency_id: agency?._id,
      };

      if (editIndex !== null) {
        response = await AppService.topnav_sub_categories.update({
          payload: { ...payload, _id: editIndex },
        });
      } else {
        response = await AppService.topnav_sub_categories.create({ payload });
      }

      if (response) {
        getSubCategories();
        setOpenModal(false);
        setFormData({ name: "", category: null, tags: [] });
        setEditIndex(null);
        toast.success("SubCategory saved successfully");
      }
    } catch (error) {
      console.error("Error saving subcategory:", error);
      toast.error("Error saving subcategory");
    }

    setLoading(false);
  };

  // Handle edit
  const handleEdit = (subCategory) => {
    setEditIndex(subCategory?._id);

    setFormData({
      name: subCategory?.name,
      category: {
        label: subCategory?.category?.name,
        value: subCategory?.category?._id,
      },
      tags: subCategory?.tags?.map((tag) => tag?._id) || [],
    });

    setOpenModal(true);
  };

  // Handle delete
  const handleDelete = async (subCategoryId) => {
    if (!window.confirm("Are you sure you want to delete this subcategory?")) return;
    try {
      await AppService.topnav_sub_categories.delete({ query: `_id=${subCategoryId}` });
      getSubCategories();
      handleRefresh();
      toast.success("Deleted successfully");
    } catch (error) {
      console.error("Error deleting subcategory:", error);
      toast.error("Error deleting subcategory");
    }
  };

  // Drag & Drop reorder
  const onDragEnd = async (result) => {
    if (!result.destination) return;

    const reordered = Array.from(subCategories);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);

    setSubCategories(reordered);
    const reorderedPayload = reordered.map((item, index) => ({
      _id: item._id,
      order: index,
    }));

    await AppService.topnav_sub_categories.ReOrderSubCategory({
      payload: { reorderItems: reorderedPayload },
    });
  };
  return (
    <div>
      <SoftBox>
        <Grid container spacing={3} rowSpacing={2}>
          <Grid item xs={12} display="flex" justifyContent="flex-end" gap={1}>
            <button
              className="btn-new-design-black"
              type="button"
              onClick={() => {
                setOpenModal(true);
                setFormData({ name: "", category: null, tags: [] });
                setEditIndex(null);
              }}
            >
              Add New SubCategory
            </button>
          </Grid>

          {/* Table Header */}
          <Grid item xs={12}>
            <div
              className="d-flex justify-content-between"
              style={{
                margin: "10px 0px",
                padding: "20px",
                borderRadius: "18px",
                background: "#F7F7F7",
              }}
            >
              <p style={{ flex: 1, fontWeight: 600 }}>Name</p>
              <p style={{ flex: 1, fontWeight: 600 }}>Category</p>
              <p style={{ flex: 1, fontWeight: 600 }}>Tags</p>
              <p style={{ flex: 1, fontWeight: 600 }}>Action</p>
            </div>

            {/* Draggable Subcategories */}
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="droppable" direction="vertical">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    {subCategories.map((subCategory, index) => (
                      <Draggable
                        key={subCategory._id}
                        draggableId={String(subCategory._id)}
                        index={index}
                      >
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className="d-flex justify-content-between"
                            style={{
                              margin: "20px 0",
                              padding: "15px 10px",
                              borderBottom: "1px solid",
                              borderColor: "#DDDDDD",
                              background: "#fff",
                              borderRadius: "10px",
                              ...provided.draggableProps.style,
                            }}
                          >
                            <div
                              style={{
                                width: 50,
                                height: 50,
                                backgroundImage: `url(${dragImage})`,
                                backgroundSize: "cover",
                                backgroundBlendMode: "invert",
                                marginTop: "-10px",
                              }}
                            ></div>
                            <p style={{ flex: 1, marginLeft: "20px" }}>{subCategory.name}</p>
                            <p style={{ flex: 1 }}>{subCategory.category?.name || "N/A"}</p>
                            <p style={{ flex: 1 }}>
                              {subCategory.tags?.map((tag) => tag.name).join(", ") || "N/A"}
                            </p>
                            <div style={{ flex: 1, display: "flex", gap: "20px" }}>
                              <img
                                onClick={() => handleEdit(subCategory)}
                                disabled={loading}
                                width={20}
                                src={editIcon}
                                style={{ cursor: "pointer" }}
                              />
                              <img
                                onClick={() => handleDelete(subCategory._id)}
                                disabled={loading}
                                width={20}
                                src={deleteIcon}
                                style={{ cursor: "pointer" }}
                              />
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </Grid>

          {/* Modal for Add/Edit */}
          {openModal && (
            <div className="modal-zz-confirm">
              <div
                className="scrollable-modal-content bg-white rounded"
                style={{ border: "0px solid", position: "relative" }}
              >
                <h2>{editIndex !== null ? "Edit SubCategory" : "Add New SubCategory"}</h2>
                <span className="crossison" onClick={() => setOpenModal(false)}>
                  X
                </span>

                <SoftBox component="form" onSubmit={handleSubmit}>
                  <FormField
                    label="SubCategory Name"
                    placeholder="Name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                  />

                  <label>Category</label>
                  <Select
                    options={categories?.map((item) => ({
                      label: item.name,
                      value: item._id,
                    }))}
                    onChange={handleCategoryChange}
                    value={formData.category}
                    placeholder="Select Category"
                  />

                  <label>Tags</label>
                  <Select
                    isMulti
                    options={tagsOptions}
                    onChange={handleTagChange}
                    value={tagsOptions.filter((tag) => formData.tags.includes(tag.value))}
                    placeholder="Select Tags"
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                    style={{
                      color: "white",
                      background: "black",
                      marginTop: "10px",
                      marginLeft: "auto",
                    }}
                  >
                    {editIndex !== null ? "Update" : "Add"}
                  </Button>
                </SoftBox>
              </div>
            </div>
          )}
        </Grid>
      </SoftBox>
    </div>
  );
}

export default SubCategoryManager;
